import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import { TIMESHEET_URL_PARAMS } from "../components/Events/Event/timesheet/constants";
import { PAYROLL_URL_PARAMS } from "../components/Events/Event/Payroll/GeneratePayroll/constants/payrollUrlParams";
import {
  normalizeTimesheetApplicantId,
  resolveTimesheetApplicantBreadcrumbLabel,
} from "../components/Events/Event/timesheet/timesheetUtils";
import {
  appendActiveUrlTabBreadcrumb,
  appendNestedDetailBreadcrumb,
  buildTimesheetApplicantsListPath,
  formatEventStatusTabLabel,
} from "../utils/Helpers";
import {
  EVENT_VIEW_DEFAULT_TAB,
  EVENT_VIEW_TABS,
  getAllowedEventViewTabs,
  resolveEventViewTabFromSearch,
  getEventViewTabLabels,
} from "../utils/eventViewTabs";

/**
 * Event view breadcrumbs: list → status → event name → [tab] → [timesheet applicant].
 */
const useEventViewBreadcrumbs = ({
  event,
  events,
  eventsListPath,
  showApplicantsTab,
  showEvaluationsTab = showApplicantsTab,
  showTimesheetTab = false,
  showReportsTab = false,
  showPayrollTab = false,
  showHeatMapTab = false,
  showClientReportsTab = false,
  isSupervisor = false,
  timesheetPortal = "admin",
  selectedRound,
  onEvaluationsBack,
  activeTab: activeTabOverride,
  onActiveTabBack,
  nestedDetailLabel,
}) => {
  const location = useLocation();
  const lang = useSelector((state) => state.locale.lang);

  const adminWorkingLogs = useSelector((state) => state.timesheet.list);
  const adminShiftsList = useSelector(
    (state) => state.applicantsTimeSheetRequest.list,
  );
  const companyWorkingLogs = useSelector(
    (state) => state.companyEventTimesheet.workingLogs.list,
  );
  const companyShiftsList = useSelector(
    (state) => state.companyEventTimesheet.workingSlots.list,
  );

  const workingLogs =
    timesheetPortal === "company" ? companyWorkingLogs : adminWorkingLogs;
  const shiftsList =
    timesheetPortal === "company" ? companyShiftsList : adminShiftsList;

  const allowedTabValues = useMemo(
    () =>
      getAllowedEventViewTabs(
        showApplicantsTab,
        showEvaluationsTab,
        isSupervisor,
        showTimesheetTab,
        showReportsTab,
        showPayrollTab,
        showHeatMapTab,
        showClientReportsTab,
      ),
    [
      showApplicantsTab,
      showEvaluationsTab,
      isSupervisor,
      showTimesheetTab,
      showReportsTab,
      showPayrollTab,
      showHeatMapTab,
      showClientReportsTab,
    ],
  );

  const activeTabFromUrl = useMemo(
    () =>
      resolveEventViewTabFromSearch(location.search, allowedTabValues) ??
      EVENT_VIEW_DEFAULT_TAB,
    [allowedTabValues, location.search],
  );

  const activeTab = activeTabOverride ?? activeTabFromUrl;

  const eventViewTabLabels = useMemo(
    () => getEventViewTabLabels(events.viewEvents),
    [events.viewEvents],
  );

  const applicantIdFromUrl = useMemo(
    () =>
      normalizeTimesheetApplicantId(
        new URLSearchParams(location.search).get(TIMESHEET_URL_PARAMS.applicant),
      ),
    [location.search],
  );

  const applicantBreadcrumbLabel = useMemo(() => {
    if (activeTab !== EVENT_VIEW_TABS.timesheet || !applicantIdFromUrl) {
      return null;
    }

    return resolveTimesheetApplicantBreadcrumbLabel({
      applicantId: applicantIdFromUrl,
      workingLogs,
      shiftsList,
      lang,
    });
  }, [
    activeTab,
    applicantIdFromUrl,
    lang,
    shiftsList,
    workingLogs,
  ]);

  const breadcrumbList = useMemo(() => {
    let list = appendActiveUrlTabBreadcrumb({
      breadcrumbs: [
        {
          path: eventsListPath,
          label: events.listEvents.listEventsBreadcrumb,
        },
        {
          path: event?.status
            ? `${eventsListPath}?tab=${event.status}`
            : eventsListPath,
          label: formatEventStatusTabLabel(
            event?.status,
            events.listEvents.eventTabs,
          ),
        },
        {
          path: "",
          label: event?.name || "",
        },
      ],
      activeTab,
      defaultTab: EVENT_VIEW_DEFAULT_TAB,
      tabLabelsByValue: eventViewTabLabels,
      viewPathname: location.pathname,
      search: location.search,
    });

    // When the tab isn't reflected in the URL (e.g. company portal), the generic
    // path-based "back" link on the event-name crumb is inert — swap it for a
    // click handler that flips the caller's local tab state instead.
    if (
      activeTabOverride != null &&
      onActiveTabBack &&
      activeTab !== EVENT_VIEW_DEFAULT_TAB
    ) {
      list = [...list];
      const backIndex = list.length - 2;
      if (backIndex >= 0) {
        list[backIndex] = {
          ...list[backIndex],
          path: "",
          isClicked: true,
          onclick: onActiveTabBack,
        };
      }
    }

    if (applicantBreadcrumbLabel) {
      list = appendNestedDetailBreadcrumb({
        breadcrumbs: list,
        detailLabel: applicantBreadcrumbLabel,
        listPath: buildTimesheetApplicantsListPath(
          location.pathname,
          location.search,
        ),
      });
    }

    if (nestedDetailLabel && activeTab === EVENT_VIEW_TABS.payroll) {
      const listParams = new URLSearchParams(location.search);
      listParams.delete(PAYROLL_URL_PARAMS.generate);
      list = appendNestedDetailBreadcrumb({
        breadcrumbs: list,
        detailLabel: nestedDetailLabel,
        listPath: `${location.pathname}?${listParams.toString()}`,
      });
    }

    if (activeTab === EVENT_VIEW_TABS.evaluations && selectedRound) {
      list = [...list];
      list[list.length - 1] = {
        ...list[list.length - 1],
        isClicked: true,
        onclick: onEvaluationsBack,
      };
      list.push({ path: "", label: selectedRound.name });
    }

    return list;
  }, [
    activeTab,
    applicantBreadcrumbLabel,
    event?.name,
    event?.status,
    eventViewTabLabels,
    events.listEvents.eventTabs,
    events.listEvents.listEventsBreadcrumb,
    eventsListPath,
    location.pathname,
    location.search,
    onEvaluationsBack,
    selectedRound,
    activeTabOverride,
    onActiveTabBack,
    nestedDetailLabel,
  ]);

  return { breadcrumbList, activeTab };
};

export default useEventViewBreadcrumbs;
